// SentinelEscortLiveTracking.jsx
// Dual map: OpenStreetMap (Leaflet) + Google Maps
// Provider from company user_map

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import Layout from "../layouts/Layout";
import { API_BASE_URL, SKIN_THEMES } from "../layouts/constants";

/* =========================================================
   GOOGLE MAPS LOADER
========================================================= */

const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;
  return document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");
  if (errorText.includes("RefererNotAllowedMapError")) {
    return "This website is not authorized for the Google Maps API key. Add this domain under the API key website restrictions.";
  }
  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }
  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }
  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }
  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }
  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Google Maps can only be loaded in a browser.")
    );
  }
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!apiKey) {
    return Promise.reject(
      new Error(
        "Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file."
      )
    );
  }
  if (googleMapsLoadingPromise) return googleMapsLoadingPromise;

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;
    const resolveOnce = () => {
      if (settled || !window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };
    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;
      reject(
        error instanceof Error
          ? error
          : new Error("Google Maps JavaScript API failed to load.")
      );
    };
    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }
      rejectOnce(
        new Error("Google Maps script loaded, but the Maps API is unavailable.")
      );
    };
    const handleScriptError = () => {
      rejectOnce(new Error("Google Maps JavaScript API network request failed."));
    };
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (e) {
          console.error(e);
        }
      }
      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };
    const existingScript = findExistingGoogleMapsScript();
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      existingScript.addEventListener("error", handleScriptError, {
        once: true,
      });
      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);
      return;
    }
    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places,geometry",
      v: "weekly",
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${queryParameters.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");
    script.onload = handleScriptLoad;
    script.onerror = () => {
      script.remove();
      handleScriptError();
    };
    document.head.appendChild(script);
  });
  return googleMapsLoadingPromise;
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();
  if (
    provider === "google" ||
    provider === "googlemaps" ||
    provider === "google_maps"
  ) {
    return "google";
  }
  return "openstreetmap";
}

// Builds a colored circular marker icon as an inline SVG data URI.
// This avoids relying on window.google.maps.SymbolPath, which can
// occasionally be unresolved right after the async script load and
// causes markers to render as a broken-image placeholder instead of
// a colored dot. Data-URI icons always render, no timing dependency.
function googleMarkerIcon(color, { size = 22, label = null, borderColor = "#ffffff" } = {}) {
  const half = size / 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<circle cx="${half}" cy="${half}" r="${half - 2}" fill="${color}" stroke="${borderColor}" stroke-width="2"/>` +
    (label
      ? `<text x="50%" y="53%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="${Math.round(
          size * 0.48
        )}" font-weight="700" fill="#ffffff">${label}</text>`
      : "") +
    `</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(half, half),
  };
}

/* =========================================================
   SHARED HELPERS
========================================================= */

const POLL_MS = 8000;
const POINT_MOVE_MS = 850;

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isNum(v) {
  return Number.isFinite(Number(v));
}

function km(m) {
  return (Number(m || 0) / 1000).toFixed(1);
}

function kph(mps) {
  return Math.round(Number(mps || 0) * 3.6);
}

function duration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function pointTime(p) {
  const t = Date.parse(p.ts || p.recorded_at || p.created_at || "");
  return Number.isFinite(t) ? t : 0;
}

function normalizePoints(points = []) {
  const seen = new Set();
  return points
    .map((p) => ({
      ...p,
      id: Number(p.id || 0),
      lat: toNum(p.lat),
      lng: toNum(p.lng),
      speed_mps: toNum(p.speed_mps) || 0,
      heading_deg: toNum(p.heading_deg) || 0,
      ts: p.ts || p.recorded_at || p.created_at || "",
      _time: pointTime(p),
    }))
    .filter((p) => isNum(p.lat) && isNum(p.lng))
    .sort((a, b) => {
      if (a._time !== b._time) return a._time - b._time;
      return a.id - b.id;
    })
    .filter((p) => {
      const key = `${p.lat.toFixed(7)},${p.lng.toFixed(7)},${p._time}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function vehicleSvg(rotation = 0) {
  return `
    <div style="transform: rotate(${rotation}deg); transform-origin:center;">
      <svg viewBox="0 0 64 32" width="52" height="52" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="10" width="30" height="12" rx="2" fill="#111827" />
        <path d="M34 10h12l6 6v6H34z" fill="#111827" />
        <circle cx="14" cy="26" r="4" fill="#1e293b" />
        <circle cx="42" cy="26" r="4" fill="#1e293b" />
      </svg>
    </div>
  `;
}

function vehicleIcon(rotation = 0) {
  return L.divIcon({
    className: "escort-vehicle-icon",
    html: vehicleSvg(rotation),
    iconSize: [52, 52],
    iconAnchor: [26, 26],
  });
}

function stoppedIcon() {
  return L.divIcon({
    className: "stopped-marker",
    html: `
      <div style="
        background:#ef4444;
        color:white;
        width:32px;
        height:32px;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        font-weight:bold;
        font-size:16px;
        border:3px solid white;
        box-shadow:0 4px 8px rgba(0,0,0,0.3);
      ">S</div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

/* =========================================================
   COMPONENT
========================================================= */

export default function SentinelEscortLiveTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const memberId = params.get("member_id") || "";
  const runId = params.get("run_id") || "";

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    user_map: "",
  });

  const isGoogleMap = useMemo(
    () => normalizeMapProvider(dashboardData.user_map) === "google",
    [dashboardData.user_map]
  );

  const routePage = useMemo(
    () => (isGoogleMap ? "/route-master" : "/route-master1"),
    [isGoogleMap]
  );

  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");
  const [pageError, setPageError] = useState("");
  const errorShownRef = useRef(false);

  // NEW: tracks whether the Google Map instance has actually finished
  // initializing. googleMapRef.current alone can't be used for this because
  // mutating a ref does not trigger a re-render, so effects that depend on
  // "is the google map ready" need a piece of state instead.
  const [googleMapReady, setGoogleMapReady] = useState(false);

  /* Leaflet refs */
  const leafletMapRef = useRef(null);
  const leafletMapBoxRef = useRef(null);
  const leafletLatestMarkerRef = useRef(null);
  const leafletStartMarkerRef = useRef(null);
  const leafletStopMarkerRef = useRef(null);
  const leafletStoppedMarkerRef = useRef(null);
  const leafletPolylineRef = useRef(null);

  /* Google refs */
  const googleMapRef = useRef(null);
  const googleMapBoxRef = useRef(null);
  const googleLatestMarkerRef = useRef(null);
  const googleStartMarkerRef = useRef(null);
  const googleStopMarkerRef = useRef(null);
  const googleStoppedMarkerRef = useRef(null);
  const googlePolylineRef = useRef(null);

  const pollRef = useRef(null);
  const animationRef = useRef(null);
  const fittedRef = useRef(false);

  const latestLatLngRef = useRef(null);
  const lastPointIdRef = useRef(null);
  const moveQueueRef = useRef([]);
  const isMovingRef = useRef(false);

  const [followLive, setFollowLive] = useState(true);
  const followRef = useRef(true);

  const [status, setStatus] = useState({
    label: "—",
    className: "bg-slate-200 text-slate-700",
  });

  const [meta, setMeta] = useState({
    title: "—",
    from: "—",
    to: "—",
    vehicle: "—",
    lastTs: "—",
    distance: "0",
    speed: "0",
    duration: "—",
    points: "0",
  });

  // ========== THIS IS THE CORRECT API CALL ==========
  const apiUrl = useMemo(() => {
    const q = new URLSearchParams({
      member_id: memberId,
      limit: "2000",
      include_segments: "1",
    });
    if (runId) q.set("run_id", runId);
    return api(`/api/sentinel_escort/live/tracking?${q.toString()}`);
  }, [memberId, runId]);

  const fetchDashboard = useCallback(async () => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(api("/api/dashboard/admin/dashboard"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      const company = json?.data?.companyDetails || {};
      setDashboardData({
        ...company,
        user_map: normalizeMapProvider(
          company.user_map || localStorage.getItem("user_map") || ""
        ),
      });
    } catch (e) {
      setPageError(e.message);
      toast.error(e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const setRunStatus = useCallback((s) => {
    s = String(s || "").toLowerCase();
    if (s === "active") {
      setStatus({
        label: "Active",
        className:
          "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]",
      });
    } else if (s === "stopped" || s === "abandoned") {
      setStatus({
        label: s.charAt(0).toUpperCase() + s.slice(1),
        className:
          "bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]",
      });
    } else if (s === "planned") {
      setStatus({
        label: "Planned",
        className:
          "bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]",
      });
    } else {
      setStatus({
        label: s || "—",
        className:
          "bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-muted)]",
      });
    }
  }, []);

  /* ---------- animation queues ---------- */

  const moveLeafletQueue = useCallback(() => {
    if (isMovingRef.current) return;
    const map = leafletMapRef.current;
    const marker = leafletLatestMarkerRef.current;
    if (!map || !marker) return;
    if (!moveQueueRef.current.length) return;

    isMovingRef.current = true;
    const point = moveQueueRef.current.shift();
    const from = marker.getLatLng();
    const to = L.latLng(point.lat, point.lng);

    marker.setIcon(vehicleIcon(point.heading_deg || 0));
    marker.setTooltipContent(point.ts || "Live");

    const start = performance.now();
    const animate = (now) => {
      const t = Math.min((now - start) / POINT_MOVE_MS, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      const lat = from.lat + (to.lat - from.lat) * ease;
      const lng = from.lng + (to.lng - from.lng) * ease;
      marker.setLatLng([lat, lng]);
      if (followRef.current) {
        map.panTo([lat, lng], { animate: false });
      }
      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        latestLatLngRef.current = to;
        isMovingRef.current = false;
        moveLeafletQueue();
      }
    };
    animationRef.current = requestAnimationFrame(animate);
  }, []);

  const moveGoogleQueue = useCallback(() => {
    if (isMovingRef.current) return;
    const map = googleMapRef.current;
    const marker = googleLatestMarkerRef.current;
    if (!map || !marker || !window.google?.maps) return;
    if (!moveQueueRef.current.length) return;

    isMovingRef.current = true;
    const point = moveQueueRef.current.shift();
    const from = marker.getPosition();
    if (!from) {
      isMovingRef.current = false;
      return;
    }
    const to = { lat: point.lat, lng: point.lng };

    marker.setTitle(point.ts || "Live");

    const start = performance.now();
    const animate = (now) => {
      const t = Math.min((now - start) / POINT_MOVE_MS, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      const lat = from.lat() + (to.lat - from.lat()) * ease;
      const lng = from.lng() + (to.lng - from.lng()) * ease;
      marker.setPosition({ lat, lng });
      if (followRef.current) {
        map.panTo({ lat, lng });
      }
      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        latestLatLngRef.current = L.latLng(to.lat, to.lng);
        isMovingRef.current = false;
        moveGoogleQueue();
      }
    };
    animationRef.current = requestAnimationFrame(animate);
  }, []);

  /* ---------- LEAFLET update ---------- */

  const updateLeafletMap = useCallback(
    (payload) => {
      const map = leafletMapRef.current;
      if (!map) return [];

      const run = payload.run || {};
      const points = normalizePoints(
        Array.isArray(payload.points) ? payload.points : []
      );
      const coords = points.map((p) => [p.lat, p.lng]);

      if (coords.length >= 2) {
        if (!leafletPolylineRef.current) {
          leafletPolylineRef.current = L.polyline(coords, {
            color: "#0284c7",
            weight: 5,
            opacity: 0.85,
            lineJoin: "round",
            lineCap: "round",
            smoothFactor: 1,
          }).addTo(map);
        } else {
          leafletPolylineRef.current.setLatLngs(coords);
        }
      }

      if (isNum(run.start_lat) && isNum(run.start_lng)) {
        const startLatLng = L.latLng(run.start_lat, run.start_lng);
        if (!leafletStartMarkerRef.current) {
          leafletStartMarkerRef.current = L.circleMarker(startLatLng, {
            radius: 8,
            color: "#22c55e",
            fillColor: "#22c55e",
            fillOpacity: 0.9,
          })
            .addTo(map)
            .bindTooltip(run.from_name || "Start");
        } else {
          leafletStartMarkerRef.current.setLatLng(startLatLng);
        }
      }

      if (isNum(run.end_lat) && isNum(run.end_lng)) {
        const endLatLng = L.latLng(run.end_lat, run.end_lng);
        if (!leafletStopMarkerRef.current) {
          leafletStopMarkerRef.current = L.circleMarker(endLatLng, {
            radius: 8,
            color: "#ef4444",
            fillColor: "#ef4444",
            fillOpacity: 0.9,
          })
            .addTo(map)
            .bindTooltip(run.to_name || "End");
        } else {
          leafletStopMarkerRef.current.setLatLng(endLatLng);
        }
      }

      if (points.length) {
        if (!leafletLatestMarkerRef.current) {
          const firstPoint = points[0];
          const firstLatLng = L.latLng(firstPoint.lat, firstPoint.lng);
          leafletLatestMarkerRef.current = L.marker(firstLatLng, {
            icon: vehicleIcon(firstPoint.heading_deg || 0),
            zIndexOffset: 999,
          })
            .addTo(map)
            .bindTooltip(firstPoint.ts || "Live");
          latestLatLngRef.current = firstLatLng;
          lastPointIdRef.current = firstPoint.id;
        }

        const newPoints = points.filter((p) => {
          if (!lastPointIdRef.current) return true;
          return Number(p.id) > Number(lastPointIdRef.current);
        });

        if (newPoints.length) {
          lastPointIdRef.current = newPoints[newPoints.length - 1].id;
          moveQueueRef.current.push(...newPoints);
          moveLeafletQueue();
        }
      }

      const latestPoint = points[points.length - 1];
      const isStopped = ["stopped", "abandoned"].includes(
        String(run.status || "").toLowerCase()
      );

      if (isStopped && latestPoint) {
        const stoppedLatLng = L.latLng(latestPoint.lat, latestPoint.lng);
        if (!leafletStoppedMarkerRef.current) {
          leafletStoppedMarkerRef.current = L.marker(stoppedLatLng, {
            icon: stoppedIcon(),
            zIndexOffset: 1000,
          })
            .addTo(map)
            .bindTooltip("Stopped Here");
        } else {
          leafletStoppedMarkerRef.current.setLatLng(stoppedLatLng);
        }
      } else if (leafletStoppedMarkerRef.current) {
        map.removeLayer(leafletStoppedMarkerRef.current);
        leafletStoppedMarkerRef.current = null;
      }

      if (!fittedRef.current && coords.length >= 2) {
        const bounds = L.latLngBounds(coords);
        if (isNum(run.start_lat) && isNum(run.start_lng)) {
          bounds.extend([run.start_lat, run.start_lng]);
        }
        if (isNum(run.end_lat) && isNum(run.end_lng)) {
          bounds.extend([run.end_lat, run.end_lng]);
        }
        if (bounds.isValid()) {
          map.fitBounds(bounds.pad(0.15));
          fittedRef.current = true;
        }
      }

      return points;
    },
    [moveLeafletQueue]
  );

  /* ---------- GOOGLE update ---------- */

  const updateGoogleMap = useCallback(
    (payload) => {
      const map = googleMapRef.current;
      if (!map || !window.google?.maps) return [];

      const run = payload.run || {};
      const points = normalizePoints(
        Array.isArray(payload.points) ? payload.points : []
      );
      const path = points.map((p) => ({ lat: p.lat, lng: p.lng }));

      if (path.length >= 2) {
        if (!googlePolylineRef.current) {
          googlePolylineRef.current = new window.google.maps.Polyline({
            map,
            path,
            strokeColor: "#0284c7",
            strokeOpacity: 0.85,
            strokeWeight: 5,
          });
        } else {
          googlePolylineRef.current.setPath(path);
        }
      }

      if (isNum(run.start_lat) && isNum(run.start_lng)) {
        const pos = {
          lat: Number(run.start_lat),
          lng: Number(run.start_lng),
        };
        if (!googleStartMarkerRef.current) {
          googleStartMarkerRef.current = new window.google.maps.Marker({
            map,
            position: pos,
            title: run.from_name || "Start",
            icon: googleMarkerIcon("#22c55e", { size: 20 }),
          });
        } else {
          googleStartMarkerRef.current.setPosition(pos);
        }
      }

      if (isNum(run.end_lat) && isNum(run.end_lng)) {
        const pos = {
          lat: Number(run.end_lat),
          lng: Number(run.end_lng),
        };
        if (!googleStopMarkerRef.current) {
          googleStopMarkerRef.current = new window.google.maps.Marker({
            map,
            position: pos,
            title: run.to_name || "End",
            icon: googleMarkerIcon("#ef4444", { size: 20 }),
          });
        } else {
          googleStopMarkerRef.current.setPosition(pos);
        }
      }

      if (points.length) {
        if (!googleLatestMarkerRef.current) {
          const firstPoint = points[0];
          googleLatestMarkerRef.current = new window.google.maps.Marker({
            map,
            position: { lat: firstPoint.lat, lng: firstPoint.lng },
            title: firstPoint.ts || "Live",
            icon: googleMarkerIcon("#111827", { size: 26 }),
            zIndex: 999,
          });
          latestLatLngRef.current = L.latLng(firstPoint.lat, firstPoint.lng);
          lastPointIdRef.current = firstPoint.id;
        }

        const newPoints = points.filter((p) => {
          if (!lastPointIdRef.current) return true;
          return Number(p.id) > Number(lastPointIdRef.current);
        });

        if (newPoints.length) {
          lastPointIdRef.current = newPoints[newPoints.length - 1].id;
          moveQueueRef.current.push(...newPoints);
          moveGoogleQueue();
        }
      }

      const latestPoint = points[points.length - 1];
      const isStopped = ["stopped", "abandoned"].includes(
        String(run.status || "").toLowerCase()
      );

      if (isStopped && latestPoint) {
        const pos = { lat: latestPoint.lat, lng: latestPoint.lng };
        if (!googleStoppedMarkerRef.current) {
          googleStoppedMarkerRef.current = new window.google.maps.Marker({
            map,
            position: pos,
            title: "Stopped Here",
            icon: googleMarkerIcon("#ef4444", { size: 30, label: "S" }),
            zIndex: 1000,
          });
        } else {
          googleStoppedMarkerRef.current.setPosition(pos);
        }
      } else if (googleStoppedMarkerRef.current) {
        googleStoppedMarkerRef.current.setMap(null);
        googleStoppedMarkerRef.current = null;
      }

      if (!fittedRef.current && path.length >= 2) {
        const bounds = new window.google.maps.LatLngBounds();
        path.forEach((p) => bounds.extend(p));
        if (isNum(run.start_lat) && isNum(run.start_lng)) {
          bounds.extend({
            lat: Number(run.start_lat),
            lng: Number(run.start_lng),
          });
        }
        if (isNum(run.end_lat) && isNum(run.end_lng)) {
          bounds.extend({
            lat: Number(run.end_lat),
            lng: Number(run.end_lng),
          });
        }
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, 48);
          fittedRef.current = true;
        }
      }

      return points;
    },
    [moveGoogleQueue]
  );

  /* ---------- refresh ---------- */

  const refresh = useCallback(async () => {
    if (!memberId) {
      toast.error("Member ID is required");
      return;
    }
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "API error");
      }
      errorShownRef.current = false;

      const payload = json.data;
      const run = payload.run || {};
      const points = isGoogleMap
        ? updateGoogleMap(payload)
        : updateLeafletMap(payload);
      const latest = points[points.length - 1] || payload.latest || {};

      setMeta({
        title: run.title || "—",
        from: run.from_name || "—",
        to: run.to_name || "—",
        vehicle: run.vehicle || "—",
        lastTs: latest.ts || "—",
        distance: km(run.distance_m || payload.distance_m),
        speed: String(kph(latest.speed_mps)),
        duration: run.start_time
          ? duration(
              Date.parse(run.end_time || new Date()) -
                Date.parse(run.start_time)
            )
          : "—",
        points: String(points.length || payload.total_points || 0),
      });
      setRunStatus(run.status);
    } catch (err) {
      console.error("Refresh error:", err);
      if (!errorShownRef.current) {
        toast.error(err.message || "Failed to fetch tracking data");
        errorShownRef.current = true;
      }
    }
  }, [
    apiUrl,
    memberId,
    isGoogleMap,
    updateLeafletMap,
    updateGoogleMap,
    setRunStatus,
  ]);

  const fitTrack = useCallback(() => {
    if (isGoogleMap) {
      const map = googleMapRef.current;
      if (!map || !window.google?.maps) return;
      const bounds = new window.google.maps.LatLngBounds();
      let has = false;
      [
        googleLatestMarkerRef,
        googleStartMarkerRef,
        googleStopMarkerRef,
        googleStoppedMarkerRef,
      ].forEach((ref) => {
        const pos = ref.current?.getPosition?.();
        if (pos) {
          bounds.extend(pos);
          has = true;
        }
      });
      const path = googlePolylineRef.current?.getPath?.();
      if (path) {
        for (let i = 0; i < path.getLength(); i++) {
          bounds.extend(path.getAt(i));
          has = true;
        }
      }
      if (has) {
        map.fitBounds(bounds, 40);
        toast("Track fitted to map", { icon: "📍" });
      } else {
        toast("No track data available", { icon: "⚠️" });
      }
      return;
    }

    const map = leafletMapRef.current;
    if (!map) return;
    const group = L.featureGroup([]);
    if (leafletLatestMarkerRef.current)
      group.addLayer(leafletLatestMarkerRef.current);
    if (leafletStartMarkerRef.current)
      group.addLayer(leafletStartMarkerRef.current);
    if (leafletStopMarkerRef.current)
      group.addLayer(leafletStopMarkerRef.current);
    if (leafletStoppedMarkerRef.current)
      group.addLayer(leafletStoppedMarkerRef.current);
    if (leafletPolylineRef.current) group.addLayer(leafletPolylineRef.current);
    const b = group.getBounds();
    if (b.isValid()) {
      map.fitBounds(b.pad(0.15));
      toast("Track fitted to map", { icon: "📍" });
    } else {
      toast("No track data available", { icon: "⚠️" });
    }
  }, [isGoogleMap]);

  const recenter = useCallback(() => {
    if (!latestLatLngRef.current) {
      toast("No live position available", { icon: "⚠️" });
      return;
    }
    if (isGoogleMap && googleMapRef.current) {
      const ll = latestLatLngRef.current;
      googleMapRef.current.panTo({
        lat: ll.lat ?? ll.lat,
        lng: ll.lng ?? ll.lng,
      });
      toast("Recenter to latest position", { icon: "📍" });
    } else if (leafletMapRef.current) {
      leafletMapRef.current.panTo(latestLatLngRef.current, {
        animate: true,
        duration: 0.5,
      });
      toast("Recenter to latest position", { icon: "📍" });
    }
  }, [isGoogleMap]);

  useEffect(() => {
    followRef.current = followLive;
  }, [followLive]);

  /* ---------- init maps ---------- */

  useEffect(() => {
    if (loading) return undefined;

    let cancelled = false;
    setMapError("");
    fittedRef.current = false;
    lastPointIdRef.current = null;
    moveQueueRef.current = [];
    isMovingRef.current = false;
    latestLatLngRef.current = null;
    setGoogleMapReady(false);

    if (isGoogleMap) {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      setMapLoading(true);
      (async () => {
        try {
          await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
          if (cancelled || !googleMapBoxRef.current) return;
          const map = new window.google.maps.Map(googleMapBoxRef.current, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            clickableIcons: false,
          });
          googleMapRef.current = map;
          setMapLoading(false);
          // FIX: flip a state flag (not just the ref) once the Google map
          // instance exists, so the data-fetch effect below — which depends
          // on googleMapReady — actually re-runs and starts polling.
          if (!cancelled) setGoogleMapReady(true);
        } catch (error) {
          console.error("Google Maps init error:", error);
          const message = getGoogleMapsErrorMessage(error);
          setMapError(message);
          setMapLoading(false);
          toast.error(message);
        }
      })();
      return () => {
        cancelled = true;
        if (pollRef.current) clearInterval(pollRef.current);
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        setGoogleMapReady(false);
        [
          googleLatestMarkerRef,
          googleStartMarkerRef,
          googleStopMarkerRef,
          googleStoppedMarkerRef,
        ].forEach((ref) => {
          if (ref.current) {
            ref.current.setMap(null);
            ref.current = null;
          }
        });
        if (googlePolylineRef.current) {
          googlePolylineRef.current.setMap(null);
          googlePolylineRef.current = null;
        }
        googleMapRef.current = null;
      };
    }

    // Leaflet
    if (googleMapRef.current) {
      [
        googleLatestMarkerRef,
        googleStartMarkerRef,
        googleStopMarkerRef,
        googleStoppedMarkerRef,
      ].forEach((ref) => {
        if (ref.current) {
          ref.current.setMap(null);
          ref.current = null;
        }
      });
      if (googlePolylineRef.current) {
        googlePolylineRef.current.setMap(null);
        googlePolylineRef.current = null;
      }
      googleMapRef.current = null;
    }

    if (!leafletMapBoxRef.current || leafletMapRef.current) return undefined;

    const map = L.map(leafletMapBoxRef.current).setView(
      [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
      DEFAULT_ZOOM
    );
    leafletMapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
    }).addTo(map);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      map.remove();
      leafletMapRef.current = null;
    };
  }, [loading, isGoogleMap]);

  useEffect(() => {
    if (loading) return undefined;
    // FIX: use the googleMapReady state flag instead of reading
    // googleMapRef.current directly. Ref mutations don't cause React to
    // re-run effects, so the old `!googleMapRef.current` check meant this
    // effect never got a second chance to run once the async Google Maps
    // script finished loading — the tracking API was simply never called.
    if (isGoogleMap && !googleMapReady && !mapError) return undefined;
    if (!isGoogleMap && !leafletMapRef.current) return undefined;

    fittedRef.current = false;
    lastPointIdRef.current = null;
    moveQueueRef.current = [];
    isMovingRef.current = false;

    refresh();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(refresh, POLL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loading, memberId, runId, isGoogleMap, mapError, googleMapReady, refresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel escort live tracking...
        </p>
      </div>
    );
  }

  return (
    <Layout
      routePage={routePage}
      dashboardData={dashboardData}
      activePage="escort"
      activeMenu="escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
        }}
      />

      <style>{`
        .leaflet-container {
          height: 100%;
          width: 100%;
        }
        .escort-vehicle-icon,
        .stopped-marker {
          background: transparent;
          border: none;
        }
        .escort-map-el {
          height: 100%;
          width: 100%;
          min-height: 550px;
        }
      `}</style>

      {pageError && (
        <div className="mb-4 p-3 rounded-xl bg-[var(--red-faint)] text-[var(--red)] border border-[var(--red-border)]">
          {pageError}
        </div>
      )}

      <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--card-border)] shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--divider)] flex gap-4 items-center flex-wrap bg-[var(--card-bg)]">
          <h3 className="font-extrabold text-base m-0 text-[var(--text-primary)]">
            Sentinel Escort Live Tracking
          </h3>

          <div className="px-3 py-1 text-xs font-extrabold rounded-full bg-[var(--surface)] text-[var(--text-primary)] border border-[var(--surface-border)]">
            {isGoogleMap ? "Google Maps" : "OpenStreetMap"}
          </div>

          <div className="text-[var(--text-secondary)]">
            Member:{" "}
            <b className="text-[var(--text-primary)]">{memberId || "—"}</b>
          </div>

          {runId && (
            <div className="text-[var(--text-secondary)]">
              Run #<b className="text-[var(--text-primary)]">{runId}</b>
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate("/sentinel_escort_list")}
            className="ml-auto px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Escort List
          </button>

          <div
            className={`px-3 py-1.5 rounded-full text-xs font-extrabold ${status.className}`}
          >
            {status.label}
          </div>
        </div>

        <div className="px-4 py-3 flex gap-4 flex-wrap bg-[var(--surface)] border-b border-[var(--divider)]">
          <Info label="Title" value={meta.title} />
          <Info label="From" value={meta.from} />
          <Info label="To" value={meta.to} />
          <Info label="Vehicle" value={meta.vehicle} />
          <Info label="Last" value={meta.lastTs} />
        </div>

        <div className="px-4 py-3 flex gap-3 flex-wrap border-b border-[var(--divider)] items-center bg-[var(--card-bg)]">
          <button
            type="button"
            onClick={() => setFollowLive((v) => !v)}
            className="px-4 py-2 rounded-xl text-white text-sm font-extrabold transition-transform hover:-translate-y-0.5"
            style={{
              background: followLive ? skin.gradient : "#64748b",
              boxShadow: followLive ? "0 4px 18px var(--accent-glow)" : "none",
            }}
          >
            {followLive ? "✓ Following Live" : "Follow Live"}
          </button>

          <button
            type="button"
            onClick={fitTrack}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Fit Track
          </button>

          <button
            type="button"
            onClick={recenter}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Recenter
          </button>

          <div className="flex-1" />

          <Info label="Distance" value={`${meta.distance} km`} />
          <Info label="Speed" value={`${meta.speed} km/h`} />
          <Info label="Duration" value={meta.duration} />
          <Info label="Points" value={meta.points} />
        </div>

        <div className="relative h-[calc(100vh-230px)] min-h-[550px] bg-[var(--surface)]">
          {!isGoogleMap && (
            <div
              ref={leafletMapBoxRef}
              className="escort-map-el absolute inset-0"
            />
          )}
          {isGoogleMap && (
            <div
              ref={googleMapBoxRef}
              className="escort-map-el absolute inset-0"
            />
          )}

          {mapLoading && (
            <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
              <p className="text-sm font-extrabold text-[var(--text-primary)]">
                Loading Google Maps...
              </p>
            </div>
          )}

          {mapError && (
            <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--surface)] p-5">
              <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 text-center shadow-xl">
                <p className="text-sm font-extrabold text-red-700">
                  Map could not be loaded
                </p>
                <p className="mt-2 text-xs font-semibold text-red-600">
                  {mapError}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Info({ label, value }) {
  return (
    <div className="text-xs text-[var(--text-muted)]">
      <span className="mr-1">{label}:</span>
      <b className="text-[var(--text-primary)]">{value || "—"}</b>
    </div>
  );
}